/* eslint jsx-a11y/no-noninteractive-tabindex: "off", jsx-a11y/no-noninteractive-element-interactions: "off" */
// The reorder cards are focusable listitems on purpose: arrow keys move
// a focused card and Enter opens it in the create form.
import React, {useRef} from 'react'
import PropTypes from 'prop-types'
import {withStyles} from '@material-ui/core'
import DragIndicatorIcon from '@material-ui/icons/DragIndicator'
import EditIcon from '@material-ui/icons/Edit'
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp'
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown'
import {useSortable} from '@dnd-kit/sortable'
import {CSS} from '@dnd-kit/utilities'

import {questionTypes} from '../../../constants/NewTest'
import {getQuestionKey, questionLabel} from '../reorderUtils'
import styles from '../../styles/ReorderList.styles'

const CARD_LABEL_MAX_LENGTH = 160

const ReorderCard = props => {
  const {classes, question, index, total, onNudge, onMoveTo, onEdit} = props
  const sortableId = getQuestionKey(question)
  const {attributes, listeners, setNodeRef, transform, transition, isDragging} = useSortable({
    id: sortableId,
  })
  const cardRef = useRef(null)

  const setRefs = node => {
    cardRef.current = node
    setNodeRef(node)
  }

  const typeLabel = questionTypes[question.choiceType]
    ? questionTypes[question.choiceType].label
    : ''
  const isEmpty = !(question.question || '').trim()
  const label = questionLabel(question, CARD_LABEL_MAX_LENGTH)

  const handleNudge = direction => {
    onNudge(index, direction)

    const target = index + direction
    if (target <= 0 || target >= total - 1) {
      // the pressed arrow is about to be disabled — keep focus on the card
      setTimeout(() => {
        if (cardRef.current) cardRef.current.focus()
      }, 0)
    }
  }

  const handleKeyDown = event => {
    if (event.target !== cardRef.current) return

    if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      event.preventDefault()
      onNudge(index, event.key === 'ArrowUp' ? -1 : 1)
      // reordering relocates this card's DOM node, which drops focus —
      // restore it so repeated arrow presses keep working
      setTimeout(() => {
        if (cardRef.current) cardRef.current.focus()
      }, 0)
    } else if (event.key === 'Enter') {
      onEdit(index)
    }
  }

  return (
    <div
      ref={setRefs}
      style={{transform: CSS.Transform.toString(transform), transition}}
      className={`${classes.card} ${isDragging ? classes.cardDragging : ''}`}
      role="listitem"
      tabIndex={0}
      aria-label={`Position ${index + 1}: ${label}`}
      onKeyDown={handleKeyDown}
      onDoubleClick={event => {
        if (!event.target.closest('select, button')) onEdit(index)
      }}
    >
      <button
        type="button"
        className={classes.grip}
        aria-label="Drag to reorder"
        title="Drag to reorder"
        {...attributes}
        {...listeners}
      >
        <DragIndicatorIcon fontSize="small" />
      </button>
      <select
        className={classes.positionSelect}
        value={index + 1}
        aria-label="Move to position"
        onChange={({target}) => onMoveTo(index, Number(target.value) - 1)}
      >
        {Array.from({length: total}, (_, position) => (
          <option key={position + 1} value={position + 1}>
            {position + 1}
          </option>
        ))}
      </select>
      <div className={classes.body}>
        <div className={`${classes.text} ${isEmpty ? classes.textEmpty : ''}`}>{label}</div>
        <div className={classes.meta}>
          {typeLabel && <span className={classes.type}>{typeLabel}</span>}
          {question.followUp && <span className={classes.badge}>+ follow-up</span>}
          {!!question.id && (
            <span className={`${classes.badge} ${classes.defaultBadge}`}>Default</span>
          )}
        </div>
      </div>
      <div className={classes.actions}>
        <div className={classes.moveButtons}>
          <button
            type="button"
            className={classes.moveButton}
            aria-label="Move up"
            disabled={index === 0}
            onClick={() => handleNudge(-1)}
          >
            <KeyboardArrowUpIcon fontSize="small" />
          </button>
          <button
            type="button"
            className={classes.moveButton}
            aria-label="Move down"
            disabled={index === total - 1}
            onClick={() => handleNudge(1)}
          >
            <KeyboardArrowDownIcon fontSize="small" />
          </button>
        </div>
        <button
          type="button"
          className={classes.editButton}
          aria-label="Edit this question"
          title="Edit this question"
          onClick={() => onEdit(index)}
        >
          <EditIcon fontSize="small" />
        </button>
      </div>
    </div>
  )
}

ReorderCard.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  question: PropTypes.shape({
    question: PropTypes.string,
    choiceType: PropTypes.number,
    followUp: PropTypes.object,
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }).isRequired,
  index: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired,
  onNudge: PropTypes.func.isRequired,
  onMoveTo: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
}

export default withStyles(styles)(ReorderCard)
